import type { PumpStatus } from "@tandemdiabetes/sugarmate-api-react-client"
import { PumpAlgorithm } from "@tandemdiabetes/sugarmate-api-react-client"
import { ExclamationFillIcon } from "@tandemdiabetes/sugarmate-react-components"
import classNames from "classnames"
import type {
  ComponentPropsWithoutRef,
  ForwardedRef,
} from "react"
import {
  useMemo,
  forwardRef,
} from "react"

import styles from "./PumpStatusBarComponent.module.scss"

import {
  PumpAlgorithmIcon,
  PumpAlgorithmStatus,
  PumpBatteryIcon,
  PumpBatteryPercentage,
  PumpCartridgeFill,
  PumpCartridgeIcon,
  PumpInsulinOnBoard,
} from "src/components"
import { usePolyglot } from "src/contexts"
import { usePumpStatusRecency } from "src/hooks"
import { DataRecency } from "src/types"

const localizationPrefix = "pages.home.pump_status_bar."

interface Props extends ComponentPropsWithoutRef<"button"> {
  pumpStatus: PumpStatus;
  // this is a temporary property for demonstrating the malfunction state
  pumpMalfunction: boolean;
}

export const PumpStatusBarComponent = forwardRef<HTMLButtonElement, Props>(
  function PumpStatusBarComponent(props: Props, propRef: ForwardedRef<HTMLButtonElement>) {
    const {
      pumpStatus,
      pumpMalfunction,
      ...buttonProps
    } = props

    const polyglot = usePolyglot()
    const pumpStatusRecency = usePumpStatusRecency(pumpStatus)

    const {
      pump_algorithm: pumpAlgorithm,
    } = pumpStatus

    const containerClassName = useMemo(
      () => classNames(
        buttonProps.className,
        styles.container,
        pumpMalfunction
          ? styles.containerMalfunction
          : pumpStatusRecency === DataRecency.Current
            ? styles.containerCurrent
            : styles.containerNotCurrent,
      ),
      [
        buttonProps.className,
        pumpStatusRecency,
        pumpMalfunction,
      ],
    )

    return (
      <button
        {...buttonProps}
        type="button"
        className={containerClassName}
        ref={propRef}
      >
        {pumpMalfunction ? (
          <>
            <div className={styles.section}>
              <ExclamationFillIcon className={styles.icon} />
              <div>
                {polyglot.t(`${localizationPrefix}malfunction`)}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={styles.section}>
              <PumpBatteryIcon
                pumpStatus={pumpStatus}
                pumpStatusRecency={pumpStatusRecency}
                className={styles.icon}
              />
              <PumpBatteryPercentage
                pumpStatus={pumpStatus}
                pumpStatusRecency={pumpStatusRecency}
              />
            </div>
            <div
              className={classNames(
                styles.section,
                styles.algorithmSection,
              )}
            >
              <PumpAlgorithmIcon
                pumpStatus={pumpStatus}
                pumpStatusRecency={pumpStatusRecency}
                className={styles.icon}
              />
              <PumpAlgorithmStatus
                pumpStatus={pumpStatus}
                pumpStatusRecency={pumpStatusRecency}
              />
            </div>
            {pumpAlgorithm === PumpAlgorithm.ControlIQ && (
              <div className={styles.section}>
                {polyglot.t(
                  `${localizationPrefix}iob`,
                  {
                    units: (
                      <PumpInsulinOnBoard
                        pumpStatus={pumpStatus}
                        pumpStatusRecency={pumpStatusRecency}
                      />
                    ),
                  },
                )}
              </div>
            )}
            <div className={styles.section}>
              <PumpCartridgeIcon
                pumpStatus={pumpStatus}
                pumpStatusRecency={pumpStatusRecency}
                className={styles.icon}
              />
              <PumpCartridgeFill
                pumpStatus={pumpStatus}
                pumpStatusRecency={pumpStatusRecency}
              />
            </div>
          </>
        )}
      </button>
    )
  },
)
